import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import {
  IDocument,
  IDossier,
  IInvolvedData,
  IInvolvedItem,
  IRelations,
  IRelationsItem
} from '../store/dossiers.models';

import {
  DOSSIERS_DATA_ENDPOINTS,
  DOSSIERS_INVOLVED_CONTEXT,
  DOSSIERS_RELATIONS_CONTEXT,
  DOSSIERS_RELATIONS_MODULE
} from '~/dossiers/dossier-detail/dossier-detail.const';
import { ILiveSearch } from '~/repositories/search/store/search.types';
import { Status } from '~/ui/cardview-grid/cardview-grid.types';
import { DetailHeader } from '~/ui/detail/detail-header/detail-header.types';

@Injectable()
export class DossiersService {
  constructor(private readonly http: HttpClient) {}

  public fetchDossier(id: string): Observable<IDossier> {
    return this.http.get<IDossier>(`${environment.BACKEND_URL}/dossiers/${id}`);
  }

  public fetchDossierData(
    id: string,
    dataKey: DOSSIERS_DATA_ENDPOINTS,
    context?: DOSSIERS_RELATIONS_CONTEXT
  ): Observable<IRelations | IDocument[] | IInvolvedData> {
    if (context) {
      return this.http.get<IRelations | IDocument[] | IInvolvedData>(
        `${environment.BACKEND_URL}/dossiers/${id}/${dataKey}/${context}`
      );
    }
    return this.http.get<IRelations | IDocument[] | IInvolvedData>(
      `${environment.BACKEND_URL}/dossiers/${id}/${dataKey}`
    );
  }

  public updateDossierHeader({ id, title, description, confidential, status }: DetailHeader): Observable<DetailHeader> {
    return this.http.put<DetailHeader>(`${environment.BACKEND_URL}/dossiers/${id}`, {
      title,
      description,
      confidential,
      statusCode: status.code
    });
  }

  public fetchAvailableStatuses(): Observable<Status[]> {
    return this.http.get<Status[]>(`${environment.BACKEND_URL}/dossiers/statuses`);
  }

  public removeDocument(dossierId: string, documentId: string): Observable<void> {
    return this.http.delete<void>(`${environment.BACKEND_URL}/dossiers/${dossierId}/documents/${documentId}`);
  }

  public getAvailableRelations(query: string, context: DOSSIERS_RELATIONS_CONTEXT): Observable<ILiveSearch> {
    return this.http.get<ILiveSearch>(`${environment.BACKEND_URL}/search/live`, {
      params: {
        q: query,
        ...DOSSIERS_RELATIONS_MODULE[context]
      }
    });
  }

  public addRelation(dossierId: string, context: string, relationId: string): Observable<IRelationsItem> {
    return this.http.put<IRelationsItem>(
      `${environment.BACKEND_URL}/dossiers/${dossierId}/relations/${context}/${relationId}`,
      {}
    );
  }

  public removeRelation(dossierId: string, context: string, relationId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.BACKEND_URL}/dossiers/${dossierId}/relations/${context}/${relationId}`
    );
  }

  public getAvailableLinks(dossierId: string): Observable<IRelationsItem[]> {
    return this.http.get<IRelationsItem[]>(`${environment.BACKEND_URL}/dossiers/${dossierId}/linkabledossiers`);
  }

  public addLink(dossierId: string, linkId: string): Observable<IRelationsItem> {
    return this.http.put<IRelationsItem>(
      `      ${environment.BACKEND_URL}/dossiers/${dossierId}/relations/${DOSSIERS_RELATIONS_CONTEXT.DOSSIER}/${linkId}
    `,
      {}
    );
  }

  public removeLink(dossierId: string, linkId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.BACKEND_URL}/dossiers/${dossierId}/relations/${DOSSIERS_RELATIONS_CONTEXT.DOSSIER}/${linkId}`
    );
  }

  public getAvailableInvolved(context: DOSSIERS_INVOLVED_CONTEXT): Observable<IInvolvedItem[]> {
    return this.http.get<IInvolvedItem[]>(`${environment.BACKEND_URL}/dossiers/involved/${context}`);
  }

  public addInvolved(
    dossierId: string,
    context: DOSSIERS_INVOLVED_CONTEXT,
    involvedId: string
  ): Observable<IInvolvedItem> {
    return this.http.put<IInvolvedItem>(
      `      ${environment.BACKEND_URL}/dossiers/${dossierId}/involved/${context}/${involvedId}
    `,
      {}
    );
  }

  public removeInvolved(dossierId: string, context: DOSSIERS_INVOLVED_CONTEXT, involvedId: string): Observable<void> {
    return this.http.delete<void>(`${environment.BACKEND_URL}/dossiers/${dossierId}/involved/${context}/${involvedId}`);
  }
}
